import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import autobind from 'common/decorators/autobind.js';
import Icon from 'core/components/Icon.js';
import {scrollHighlightedIntoView} from 'core/utils.js';

export default
@observer
class AutocompleteSuggestions extends React.Component {
    static propTypes = {
        // Extra class for the list when there are no suggestions. The
        // directory widget's stylesheet expects `no-results` here.
        emptyListClassName: PropTypes.string,
        handleSelectResult: PropTypes.func,
        hideResults: PropTypes.func.isRequired,
        highlightResults: PropTypes.bool,
        listClassName: PropTypes.string,
        // Returns the whole empty-state <li>, replacing the default below.
        // The directory widget needs a bare <li> with plain text: the default
        // wraps the text in a <p>, whose margins make the row 32px taller
        // than the Knockout original's.
        renderEmpty: PropTypes.func,
        // Called with a result to render its icon, replacing the inline SVGs
        // below. The directory widget passes Font Awesome glyphs, which is
        // what its Knockout dropdown used and what its stylesheet's
        // `.text-blue` / `.text-orange` are there for.
        renderIcon: PropTypes.func,
        store: PropTypes.object.isRequired,
        uniqueId: PropTypes.string.isRequired,
        // Optional element wrapping the list. The directory widget needs
        // `autocomplete-results-holder` here: style.less scopes every
        // dropdown rule under it - the white background, border, absolute
        // positioning and max-height scroll all come from
        // `.hs-field .autocomplete-results-holder ul`. Without the wrapper
        // the list renders unstyled and pushes the form down the page.
        wrapperClassName: PropTypes.string,
    };

    static defaultProps = {
        listClassName: 'search-results',
    };

    listRef = React.createRef();

    /*
     * `autocomplete.js:167` subscribed to the selected index and scrolled the
     * list to follow it; the port dropped it, so the highlight walked off the
     * bottom (HS-11432). The index lives on an observable store rather than
     * in props, so the previous value is tracked here - acting on every
     * render instead would fight a member scrolling the list by hand while
     * nothing is highlighted.
     */
    lastHighlightedIndex = -1;

    componentDidUpdate() {
        const {highlightedResultIndex} = this.props.store;
        if (highlightedResultIndex === this.lastHighlightedIndex) {
            return;
        }
        this.lastHighlightedIndex = highlightedResultIndex;
        const list = this.listRef.current;
        // `.selected` is the class renderSuggestions() puts on the
        // highlighted row, and what the widget stylesheets shade.
        scrollHighlightedIntoView(list, list?.querySelector('li.selected'));
    }

    @autobind
    handleClick(result) {
        const {handleSelectResult, store} = this.props;
        if (handleSelectResult) {
            handleSelectResult(result);
        }
        store.updateStore({
            value: result.name,
            selected: result,
            showResults: false,
        });
    }

    @autobind
    handleMouseOver(index) {
        this.props.store.updateStore({
            highlightedResult: index,
        });
    }

    renderIcon(result) {
        if (this.props.renderIcon) {
            return this.props.renderIcon(result);
        }
        switch (result.type) {
            case 'locality':
                return <Icon name="mapPin" />;
            case 'hospital':
                return <Icon name="hospital" />;
            default:
                return null;
        }
    }

    @autobind
    handleHighlight(text) {
        const {value} = this.props.store;

        const trimmedSearchString = value.trim();
        if (trimmedSearchString) {
            const words = trimmedSearchString
                .split(/\s+/)
                .reduce((acc, word) => {
                    // restrict to alphanumeric characters
                    const newWord = word.replace(/[^a-zA-Z0-9 ]/g, '');
                    if (newWord) {
                        return [...acc, newWord];
                    }
                    return acc;
                }, []);
            const pattern = new RegExp(`(${words.join('|')})`, 'gi');
            return text.replace(pattern, '<span class="highlight">$&</span>');
        }
        return text;
    }

    renderSuggestions() {
        const {highlightedResultIndex, suggestions} = this.props.store;
        return suggestions.map((result, i) => (
            <li
                className={i === highlightedResultIndex ? 'selected' : ''}
                id={result.id}
                key={result.id}
                onClick={() => this.handleClick(result)}
                onMouseOver={() => this.handleMouseOver(i)}
                role="option"
            >
                {this.renderIcon(result)}
                {this.props.highlightResults ? (
                    <p
                        className="button-handle"
                        dangerouslySetInnerHTML={{
                            __html: this.handleHighlight(result.name),
                        }}
                    />
                ) : (
                    <p className="button-handle">{result.name}</p>
                )}
            </li>
        ));
    }

    renderEmptyHandler() {
        const {renderEmpty} = this.props;
        if (renderEmpty) {
            return renderEmpty();
        }
        const {value} = this.props.store;
        return (
            <li className="no-match">
                <p>{`No matching results for "${value}"`}</p>
            </li>
        );
    }

    render() {
        const {
            isStateStore,
            suggestions,
            showResults,
            value,
        } = this.props.store;

        if (!showResults || (!value && !isStateStore)) {
            return null;
        }

        const {emptyListClassName, listClassName, wrapperClassName} = this.props;
        const empty = !suggestions.length;
        const listClasses =
            empty && emptyListClassName
                ? `${listClassName} ${emptyListClassName}`
                : listClassName;
        const list = (
            <ul
                aria-labelledby={this.props.uniqueId}
                className={listClasses}
                id={`${this.props.uniqueId}-list`}
                ref={this.listRef}
                role="listbox"
                data-testid="autocomplete-locality"
            >
                {suggestions.length
                    ? this.renderSuggestions()
                    : this.renderEmptyHandler()}
            </ul>
        );

        if (!wrapperClassName) {
            return list;
        }
        return <div className={wrapperClassName}>{list}</div>;
    }
}
